import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  TextField,
  CircularProgress,
} from '@mui/material';
import { fetchLocations, addLocation, updateLocation, deleteLocation } from '../api/superAdmin';

interface Location {
  id: number;
  name: string;
  city: string;
  province: string;
  adminName: string;
  adminEmail: string;
  adminPhone?: string;
}

interface NewLocation {
  name: string;
  city: string;
  province: string;
  adminName: string;
  adminEmail: string;
  adminPhone?: string;
  adminPassword: string; // Required for new locations
}

const LocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState<Partial<Location & { adminPassword?: string }>>({
    name: '',
    city: '',
    province: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    adminPassword: '',
  });

  const loadLocations = async () => {
    setLoading(true);
    try {
      const data = await fetchLocations(); // This will call the axios-based API
      setLocations(data.locations || []); // Ensure `data.locations` exists and is used
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]); // Default to an empty array on error
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadLocations();
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (form.id) {
        // Update an existing location
        await updateLocation(form.id, form);
      } else {
        // Add a new location
        const newLocation: NewLocation = {
          name: form.name || '',
          city: form.city || '',
          province: form.province || '',
          adminName: form.adminName || '',
          adminEmail: form.adminEmail || '',
          adminPhone: form.adminPhone || '',
          adminPassword: form.adminPassword || '', // Ensure this is included
        };
        await addLocation(newLocation);
      }
      setOpenModal(false);
      loadLocations();
    } catch (err: any) {
      console.error('Error submitting location:', err.response?.data || err.message);
      alert(`Failed to submit location: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };
  

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await deleteLocation(id);
      loadLocations();
    } catch (err) {
      console.error('Error deleting location:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Button
        variant="contained"
        onClick={() => {
          setForm({
            name: '',
            city: '',
            province: '',
            adminName: '',
            adminEmail: '',
            adminPhone: '',
            adminPassword: '',
          });
          setOpenModal(true);
        }}
        sx={{ mb: 2 }}
      >
        Add Location
      </Button>
      {loading ? (
        <CircularProgress />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Province</TableCell>
              <TableCell>Admin Name</TableCell>
              <TableCell>Admin Email</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((location) => (
              <TableRow key={location.id}>
                <TableCell>{location.name}</TableCell>
                <TableCell>{location.city}</TableCell>
                <TableCell>{location.province}</TableCell>
                <TableCell>{location.adminName}</TableCell>
                <TableCell>{location.adminEmail}</TableCell>
                <TableCell>
                <Button
  onClick={() => {
    setForm({
      name: location.name || '',
      city: location.city || '',
      province: location.province || '',
      adminName: location.adminName || '',
      adminEmail: location.adminEmail || '',
      adminPhone: location.adminPhone || '',
    });
    setOpenModal(true);
  }}
>
  Edit
</Button>

                  <Button
                    color="error"
                    onClick={() => handleDelete(location.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Modal for Add/Edit Location */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <TextField
            label="Name"
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="City"
            value={form.city || ''}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Province"
            value={form.province || ''}
            onChange={(e) => setForm({ ...form, province: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Admin Name"
            value={form.adminName || ''}
            onChange={(e) => setForm({ ...form, adminName: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Admin Email"
            type="email"
            value={form.adminEmail || ''}
            onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Admin Phone"
            value={form.adminPhone || ''}
            onChange={(e) => setForm({ ...form, adminPhone: e.target.value })}
            fullWidth
            margin="normal"
          />
          {!form.id && (
            <TextField
              label="Admin Password"
              type="password"
              value={form.adminPassword || ''}
              onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
              fullWidth
              margin="normal"
            />
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            sx={{ mt: 2 }}
          >
            Save
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default LocationsPage;
